import type { InviteInfo, LoginResponse, MeResponse, SecondFactorChallenge } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { SecondFactorStep } from '../../components/SecondFactor';
import { ApiError, api } from '../../lib/api';
import { date } from '../../lib/format';
import { useSetMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { AuthLayout } from './AuthLayout';

export function InvitePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const navigate = useNavigate();
  const setMe = useSetMe();
  // Учётная запись уже была и защищена вторым фактором: после пароля — код (ДС-12)
  const [challenge, setChallenge] = useState<SecondFactorChallenge['secondFactor'] | null>(null);
  const info = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InviteInfo>(`/auth/invites/${encodeURIComponent(token)}`),
    retry: false,
  });

  // Имя из приглашения — начальное значение: если человек его не правит, уходит оно, а не пустая строка
  const [fullName, setFullName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (info.isLoading)
    return (
      <AuthLayout title="Приглашение">
        <Spinner />
      </AuthLayout>
    );
  if (info.error || !info.data) {
    return (
      <AuthLayout title="Приглашение">
        <EmptyState
          title="Ссылка не работает"
          action={
            <Link
              to="/login"
              className="text-[14px] font-medium text-accent-text underline underline-offset-4"
            >
              Перейти ко входу
            </Link>
          }
        >
          Приглашение уже использовано, отозвано или истёк его срок. Попросите администратора отправить новое.
        </EmptyState>
      </AuthLayout>
    );
  }
  const inv = info.data;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setError(null);
    if (!inv.existingUser && password !== repeat) {
      setErrors({ repeat: 'Пароли не совпадают' });
      return;
    }
    setBusy(true);
    try {
      const r = await api<LoginResponse>(`/auth/invites/${encodeURIComponent(token)}/accept`, {
        body: {
          fullName: inv.existingUser ? (inv.fullName ?? 'Сотрудник') : (fullName ?? inv.fullName ?? ''),
          password,
        },
      });
      if ('secondFactor' in r) {
        setChallenge(r.secondFactor);
        return;
      }
      done(r);
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fieldErrors());
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function done(me: MeResponse) {
    setMe(me);
    // Первым делом — «С чего начать»: шаги и порядок работы по правам человека (БЗ-31)
    navigate({ to: '/start' });
  }

  if (challenge)
    return (
      <AuthLayout title="Код подтверждения" subtitle="Пароль верный. Подтвердите вход кодом.">
        <SecondFactorStep challenge={challenge} onDone={done} onCancel={() => navigate({ to: '/login' })} />
      </AuthLayout>
    );

  return (
    <AuthLayout
      title={`Приглашение в «${inv.tenantName}»`}
      subtitle={
        <>
          {inv.invitedBy ? `${inv.invitedBy} приглашает вас в портал. ` : ''}Почта:{' '}
          <b className="text-ink">{inv.email}</b>. Ссылка действует до {date(inv.expiresAt)}.
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
        {inv.existingUser ? (
          <Field
            label="Пароль вашей учётной записи"
            htmlFor="password"
            hint="У вас уже есть вход в портал — подтвердите его паролем."
            error={errors.password}
          >
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        ) : (
          <>
            <Field label="Имя и фамилия" htmlFor="fullName" error={errors.fullName}>
              <Input
                id="fullName"
                autoComplete="name"
                value={fullName ?? inv.fullName ?? ''}
                onChange={(e) => setFullName(e.target.value)}
              />
            </Field>
            <Field
              label="Придумайте пароль"
              htmlFor="password"
              hint="Не короче 10 символов, буквы и цифры"
              error={errors.password}
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            <Field label="Пароль ещё раз" htmlFor="repeat" error={errors.repeat}>
              <Input
                id="repeat"
                type="password"
                autoComplete="new-password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
              />
            </Field>
          </>
        )}
        <ErrorBox error={Object.keys(errors).length ? null : error} />
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          Принять приглашение
        </Button>
      </form>
    </AuthLayout>
  );
}
